// SSE Event Types
export type SSEEventType = 'task_id' | 'status' | 'progress' | 'output' | 'complete' | 'error';

// SSE Event Data Interfaces
export interface SSETaskIdData {
  task_id: string;
  cancel_id?: string;
}

export interface SSEStatusData {
  status: string;
  message: string;
}

export interface SSEProgressData {
  progress: number; // 0-100
  message: string;
  step: number;
  total_steps: number;
}

export interface SSEOutputData {
  output: string;
  timestamp?: number;
}

export interface SSECompleteData {
  status: string;
  result: string;
  duration: number; // in seconds
  finish_reason: string;
  task_id?: string;
}

export interface SSEErrorData {
  error: string;
  error_type?: string;
  message?: string;
  status?: string;
}

// Union type for SSE event data
export type SSEEventData =
  | SSETaskIdData
  | SSEStatusData
  | SSEProgressData
  | SSEOutputData
  | SSECompleteData
  | SSEErrorData;

// SSE Event interface
export interface SSEEvent<T = SSEEventData> {
  type: SSEEventType;
  data: T;
}

// SSE Stream Parser - async generator that yields parsed SSE events
export async function* parseSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>
): AsyncGenerator<SSEEvent> {
  const decoder = new TextDecoder();
  let buffer = '';
  let eventType: string | null = null;
  let eventData: string | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line === '') {
        // Empty line = end of event
        if (eventType && eventData) {
          try {
            yield {
              type: eventType as SSEEventType,
              data: JSON.parse(eventData)
            };
          } catch {
            // Invalid JSON, skip
          }
        }
        eventType = null;
        eventData = null;
      } else if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        eventData = line.slice(5).trim();
      }
    }
  }

  // Handle remaining event in buffer
  if (eventType && eventData) {
    try {
      yield {
        type: eventType as SSEEventType,
        data: JSON.parse(eventData)
      };
    } catch {
      // Invalid JSON, skip
    }
  }
}
